import { INavLink } from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import API, {
	EntityAPI,
	IEntityDocumentResult,
	IEntityTypeUserInfo,
	IUserConfigPackage,
} from '../../API';
import { LegalEntityDocumentState } from '../../outlook/LegalEntityDocument';
import { LocationLineItemCtrlState } from '../../outlook/LocationLineItemCtrl';
import { CompletionStatus, OutlookFileDocumentsState } from '../../outlook/OutlookFileDocumentsDlg';
import { IContentDisplayItem } from './LegalEntityDocumentsState';
import { TemplateDocumentsListState } from '../../components/controls/TemplateDocumentsList';
import { OutlookDragDropManager } from '../../outlook/OutlookDragDropManager';
import { PopupWindow } from '../PopupWindow';
import { DragDropTargetState } from '../../outlook/DragDropTarget';

export enum OutlookMessageMode {
	None,
	Read,
	Compose
}

export interface IOutlookItemInfo {
	from?: Office.EmailAddressDetails;
	recipients: Office.EmailAddressDetails[];
	subject: string;
	id?: string; //If it's a draft there might not be an ID yet
	conversationId?: string;
	internetMessageId?: string;
}

export interface IEmailStoredFilingInfo extends IEmailItemStoredFilingInfo {
	attachments: IAttachmentStoredFilingInfo[];
}

export interface IAttachmentStoredFilingInfo
	extends IEmailItemStoredFilingInfo {
	id: string;
}

export interface IEmailItemStoredFilingInfo {
	filingMessage: string;
	filingUrl: string;
	filedUrl: string;
	filedById: string;
	filedByDate: Date;
	entityId: string;
	entityType: string;
}


export interface IDragDroppedFile {
	name: string;
	fileContent: Blob;
}

export interface IDragLocation {
	pageY: number;
	pageX: number;
}

export interface IInitializationContext {
	activity: string; //At the moment, only supports 'configureSendAndFile'
}

//Commands that perform actions in the Outlook app interface (e.g. getting message details)
export interface IOutlookAppCommands {
	setSendAndFileLocation(entityType: IEntityTypeUserInfo | undefined, entityId: string | null, url: string) : Promise<void>;
	getInitializationContext: () => Promise<IInitializationContext>;
	getDragDropManager: () => OutlookDragDropManager;
	onItemChanged: (callback: () => void) => void; //Notifies that the selected message(s) have changed
	onItemInfoChanged: (callback: () => void) => void; //A recipient is added/removed
	getMessageMode: () => OutlookMessageMode;

	//Gets info about the current item. This could be the draft (in compose mode) or the selected message (in read mode)
	getActiveItemInfo: () => Promise<IOutlookItemInfo>;
	getSubject: () => Promise<string>;
	setSubject: (subject:string) => Promise<void>;
	getAttachments: () => Promise<Office.AttachmentDetails[]>;
	getAttachmentContent(
		id: string,
	): Promise<Office.AttachmentContent | undefined>;
	getDraftItemId(): Promise<string>;
	insertHTMLLink: (link: string) => void;
	insertFileAttachment: (name: string, uri: string) => void;
	useAsEmailTemplate: (to: string[], subject: string, bodyHtml: string) => void;
	replyInline: (to:string, from: string, sent:Date|null, subject: string, bodyHtml: string) => Promise<boolean>;
	setCategory: (
		name: string,
		color: Office.MailboxEnums.CategoryColor,
	) => Promise<void>;
	addNotificationMessage: (
		name: string,
		type: Office.MailboxEnums.ItemNotificationMessageType,
	) => void;
	clearNotificationMessage: () => void;
	refreshFiledNotificationMessage: () => Promise<void>;

	getMessageCustomProps: () => Promise<IEmailStoredFilingInfo>;
	setMessageCustomProps: (info: IEmailStoredFilingInfo) => Promise<void>;
	onMessageCustomPropsUpdated: (
		callback: (props: IEmailStoredFilingInfo) => void,
	) => void;

	launchWindow: (url: string, onClose?: () => void) => Promise<void>;
}

//Commands that perform actions in our app
export interface IContextCommands {
	pin: (entityType: string, id: string, url: string) => Promise<void>;
	unpin: (entityType: string, id: string, url: string) => Promise<void>;
	isPinned(entityType: string, id: string, url: string): boolean;
}

export default class OutlookLocationContentsViewerState {

	public documentStates: LegalEntityDocumentState[] = [];
	public folderStates: OutlookLocationContentsViewerState[] = [];

	public error: string = '';
	public searchText: string = '';
	protected userConfig: IUserConfigPackage;
	public entityId: string | null = null;

	public loading: boolean = false;
	public loaded: boolean = false;

	public title: string;
	public commands: IOutlookAppCommands;
	entityType?: IEntityTypeUserInfo;
	ctxCommands: IContextCommands;
	templates: TemplateDocumentsListState;
	outlookFiling: OutlookFileDocumentsState;
	lineItem: LocationLineItemCtrlState;
	dnd: DragDropTargetState;

	public isFileable: boolean;

	//URL being viewed
	public url: string = '';

	//URL to the native metadata for the item
	public nativeMetadataUrl: string = '';

	constructor(
		title: string,
		url: string,
		nativeMetadataUrl: string,
		entityType: string,
		id: string,
		userConfig: IUserConfigPackage,
		commands: IOutlookAppCommands,
		ctxCommands: IContextCommands
	) {
		makeAutoObservable(this);


		//Only allow filing at this level if it's an entity. It could be a site. Equally, it could be a doc lib - we don't correctly account for that yet
		this.isFileable = entityType !== null;

		this.title = title;
		this.url = url;
		this.nativeMetadataUrl = nativeMetadataUrl;
		this.entityId = id;
		this.userConfig = userConfig;
		this.entityType = userConfig.entityTypes.getConfig(entityType);
		this.commands = commands;
		this.ctxCommands = ctxCommands;
		this.title = title;
		this.templates = new TemplateDocumentsListState(userConfig);
		this.outlookFiling = new OutlookFileDocumentsState(
			entityType,
			userConfig,
			commands,
		);
		this.lineItem = new LocationLineItemCtrlState(
			commands,
			this.buildNavLinks.bind(this),
			title
		);

		this.lineItem.setIconInfo({ icon: 'FolderHorizontal' } as any);

		this.dnd = new DragDropTargetState(commands.getDragDropManager());
		this.dnd.onHover(() => {
			this.lineItem.toggleExpanded(true);
		});
		this.dnd.onDrop((files) => {
			return this.startSaveFilesToHere(files);
		});
		
		this.commands.getInitializationContext().then(async (ctx) => {
			if(ctx?.activity === 'configureSendAndFile') {
				this.setIsSendAndFileMode(true);
			}
		});
	}
	
	public isSendAndFile: boolean = false;
	setIsSendAndFileMode(arg0: boolean) {
		this.isSendAndFile = arg0;
	}

	private searchTimeout: NodeJS.Timeout | null = null;

	public setSearchText(newValue: string) {
		this.searchText = newValue;

		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}

		this.searchTimeout = setTimeout(() => {
			this.load();
		}, 300);
	}

	public setEntityId(id: string) {
		if (this.entityId !== id) {
			this.documentStates = [];
		}

		this.entityId = id;
	}

	public async ensureInitialLoaded(): Promise<void> {
		if (!this.loading && !this.loaded && this.lineItem.isExpanded) {
			this.load();
		}
	}

	public setResult(result: IEntityDocumentResult) {
		let documents = (result.documents || [])
			.map((f) => f as IContentDisplayItem)
			.map((f) => {
				f.type = 'document';
				return f;
			});
		let folders = (result.folders || [])
			.map((f) => f as IContentDisplayItem)
			.map((f) => {
				f.type = 'folder';
				return f;
			});

		if (!this.url) {
			this.url = result.webUrl;
		}

		this.documentStates = documents.map(
			(d) => new LegalEntityDocumentState(d, this.userConfig, this.commands, this.entityType),
		).sort((a, b) => (a.document?.name || '').toLowerCase().localeCompare((b.document?.name || '').toLowerCase()));

		// Sort the folder states by name
		this.folderStates = folders.map((f) => {
			let result = new OutlookLocationContentsViewerState(
				f.name,
				f.webUrl,
				'',
				this.entityType?.id || '',
				this.entityId || '',
				this.userConfig,
				this.commands,
				this.ctxCommands
			);
			result.setEntityId(this.entityId || '');
			result.url = f.webUrl;
			return result;
		}).sort((a, b) => (a.title || '').toLocaleLowerCase().localeCompare((b.title || '').toLocaleLowerCase()));

		this.loaded = true;
		this.setLoading(false);
	}

	public async load(): Promise<void> {
		this.setLoading(true);

		let api = new API(await this.userConfig.getToken());

		let result: IEntityDocumentResult | null = null;

		try {
			if (this.searchText) {
				//We aren't navigating into a subfolder, but we might be doing a search
				result = await api.documentsByFolder(this.url, false, this.searchText);
			} else if (this.url) {
				//We have a url, it could be a site or a library or a subfolder, either from a site or entity
				result = await api.documentsByFolder(this.url, false);
			} else if (this.entityType?.id && this.entityId) {
				//Get the documents for this entity
				let entityApi = EntityAPI.fromEntityInfo(this.entityType, await this.userConfig.getToken())
				result = await entityApi.documents(this.entityId, false);
			}

			if (result) {
				this.setResult(result);
			}

		} catch (e) {
			this.setResult({
				documents: [],
				folders: [],
				webUrl: '',
			});
			this.setError('Failed to load location ' + this.url);
			console.log(e);
		}
	}

	public setError(e: string) {
		this.error = e;
	}

	public setLoading(isLoading: boolean) {
		this.loading = isLoading;
	}


	private buildNavLinks(): INavLink[] {
		let navLinks: INavLink[] = [];

		let mode = this.commands.getMessageMode();

		if(mode === OutlookMessageMode.Compose) {
			navLinks.push({
				key: 'sendAndfileHereItem',
				name: 'File here on send',
				icon: 'CloudUpload',
				url: '',
				onClick: () => {
					this.commands.setSendAndFileLocation(this.entityType, this.entityId, this.url);
					this.lineItem.ctxMenu.toggleCommands(false);
				},
			});
		}
		

		if (this.entityType && this.entityId) {
			navLinks.push({
				key: 'viewEntityItem',
				name: 'Properties',
				icon: 'ReadingMode',
				url: '',
				onClick: (e) => {
					if (this.nativeMetadataUrl) {
						//displaying a sharepoint window doesn't work in outlook desktop, argh
						//this.commands.launchWindow(this.nativeMetadataUrl);

						//We can't even redirect to it. double argh
						//url = `${window.location.origin}/#redirect/${encodeURIComponent(this.nativeMetadataUrl)}`;

						//Have to resort to opening in a new window
						PopupWindow.show(this.nativeMetadataUrl + "&helmUX=1");
					} else {
						let url = `${window.location.origin}/#entityTab/${this.entityType?.id}/${this.entityId}`;
						this.commands.launchWindow(url);
					}

					this.lineItem.ctxMenu.toggleCommands(false);
				},
			});
		}

		if (!this.lineItem.isExpanded) {
			navLinks.push({
				key: 'showContentsItem',
				name: 'Show contents',
				icon: 'OpenFolderHorizontal',
				url: '',
				onClick: (e) => {
					this.lineItem.toggleExpanded(true);
					this.lineItem.ctxMenu.toggleCommands(false);
				},
			});
		}

		navLinks.push({
			key: 'fileHereItem',
			name: 'File here',
			icon: 'CloudUpload',
			url: '',
			onClick: () => {
				this.startFileCurrentSelectedItemsToHere();
				this.lineItem.ctxMenu.toggleCommands(false);
			},
		});

		if (this.ctxCommands.isPinned(this.entityType?.id || '', this.entityId || '', this.url || '')) {
			navLinks.push({
				key: 'unpinLocation',
				name: 'Remove from favourites',
				icon: 'Unpin',
				url: '',
				onClick: (e) => {
					this.ctxCommands.unpin(this.entityType?.id || '', this.entityId || '', this.url);
					this.lineItem.ctxMenu.toggleCommands(false);
				},
			});
		} else {
			navLinks.push({
				key: 'pinLocation',
				name: 'Add to favourites',
				icon: 'Pin',
				url: '',
				onClick: (e) => {
					this.ctxCommands.pin(this.entityType?.id || '', this.entityId || '', this.url);
					this.lineItem.ctxMenu.toggleCommands(false);
				},
			});
		}

		if (this.entityType?.sharePointTemplateDocumentsLibraryUrl) {
			navLinks.push({
				key: 'addDocumentItem',
				name: 'New document',
				icon: 'Document',
				url: '',
				onClick: () => {
					this.templates.show(this.entityType?.id || '', this.entityId || '', this.url);
					this.templates.onComplete(() => {
						this.load();
						this.lineItem.toggleExpanded(true);
					});
					this.lineItem.ctxMenu.toggleCommands(false);
				},
			});
		}


		if (this.url) {
			navLinks.push({
				key: 'openLocationItem',
				name: 'Open location',
				icon: 'NavigateExternalInline',
				url: '',
				onClick: (e) => {
					window.open(this.url, '_blank');
					this.lineItem.ctxMenu.toggleCommands(false);
				},
			});
		}

		return navLinks;
	}

	private startSaveFilesToHere(files: IDragDroppedFile[]): Promise<void> {
		return this.outlookFiling.setVisibleAndSave(
			this.entityId || '',
			this.url,
			this.title || this.url,
			files,
			(s: CompletionStatus) => {
				if (s === CompletionStatus.AllSuccess || s === CompletionStatus.PartialSuccess) {

					if (!this.lineItem.isExpanded) {
						this.lineItem.toggleExpanded(true);
					} else {
						//It's already expanded, so reload it
						this.load();
					}
				}
			}
		);
	}

	private async startFileCurrentSelectedItemsToHere(): Promise<void> {
		let currentMessage = await this.commands.getActiveItemInfo();
		let currentAttachments = await this.commands.getAttachments();
		await this.startFileItemsToHere(currentMessage, currentAttachments);
	}

	private startFileItemsToHere(message?: IOutlookItemInfo, attachments?: Office.AttachmentDetails[]): Promise<void> {
		return this.outlookFiling.setVisible(
			true,
			this.entityId || '',
			this.url,
			this.title || this.url,
			message,
			attachments,
			(s: CompletionStatus) => {
				if (s === CompletionStatus.AllSuccess || s === CompletionStatus.PartialSuccess) {
					this.lineItem.toggleExpanded(true);
				}
			}
		);
	}
}
